import DashboardLayout from '@/components/DashboardLayout'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { router } from '@inertiajs/react'
import {
  Trash2,
  CheckCircle,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  UserPlus,
  Pencil,
  Users,
  CreditCard,
  KeyRound,
  TrendingDown,
} from 'lucide-react'
import { applyCpfMask } from '@/components/ui/cpf-input'
import { FilterToolbar } from '@/components/filters/FilterToolbar'
import { PerPageSelect } from '@/components/filters/PerPageSelect'
import { EmptyResults } from '@/components/filters/EmptyResults'
import type {
  ActiveChip,
  FilterSection,
  FilterValues,
  QuickFilter,
} from '@/components/filters/types'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { useState } from 'react'

interface Dependent {
  id: number
  name: string
  email: string
  phone: string
  verified: boolean
  has_active_package?: boolean
  active_package_name?: string
}

interface User {
  id: number
  name: string
  email: string
  phone: string
  role: string
  verified: boolean
  document?: string | null
  created_at: string
  has_active_package?: boolean
  active_package_name?: string
  lessons_completed?: number
  lessons_total?: number
  lessons_remaining?: number
  dependents?: Dependent[]
  dependents_count?: number
  wavecoin_balance?: number | null
}

interface School {
  id: number
  name: string
}

interface Pagination {
  current_page: number
  total_pages: number
  total_count: number
  per_page: number
}

interface Filters {
  active?: string
  search?: string
  sort_by: string
  sort_direction: string
  has_dependents?: string
  has_document?: string
  has_birth_date?: string
  email_status?: string
  lessons_left?: string
}

// Espelha NextfitStudentImporter::PLACEHOLDER_EMAIL_DOMAIN: e-mail que a importação
// inventou porque o aluno não tinha um. Quem tem esse e-mail só loga por CPF.
const IMPORT_EMAIL_DOMAIN = '@import.local'

// Filtros do dialog, agrupados por assunto. 'all' é o sentinela de "sem filtro":
// o valor vazio só existe do lado da URL.
const ADVANCED_FILTER_SECTIONS: readonly FilterSection[] = [
  {
    title: 'Cadastro',
    hint: 'O que a importação do NextFit deixou incompleto',
    fields: [
      {
        key: 'has_document',
        label: 'CPF',
        options: [
          { value: 'all', label: 'Todos' },
          { value: 'true', label: 'Com CPF' },
          { value: 'false', label: 'Sem CPF' },
        ],
      },
      {
        key: 'email_status',
        label: 'E-mail',
        options: [
          { value: 'all', label: 'Todos' },
          { value: 'real', label: 'Real' },
          { value: 'placeholder', label: 'Provisório' },
        ],
      },
      {
        key: 'has_birth_date',
        label: 'Data de nascimento',
        options: [
          { value: 'all', label: 'Todos' },
          { value: 'true', label: 'Com data' },
          { value: 'false', label: 'Sem data' },
        ],
      },
    ],
  },
  {
    title: 'Pacote e aulas',
    fields: [
      {
        key: 'active',
        label: 'Status do pacote',
        options: [
          { value: 'all', label: 'Todos' },
          { value: 'true', label: 'Ativos' },
          { value: 'false', label: 'Inativos' },
        ],
      },
      {
        key: 'lessons_left',
        label: 'Aulas restantes',
        options: [
          { value: 'all', label: 'Todas' },
          { value: 'ending', label: 'Acabando (1 a 2)' },
          { value: 'healthy', label: 'Com folga (3+)' },
        ],
      },
    ],
  },
  {
    title: 'Família',
    fields: [
      {
        key: 'has_dependents',
        label: 'Dependentes',
        options: [
          { value: 'all', label: 'Todos' },
          { value: 'true', label: 'Com dependentes' },
          { value: 'false', label: 'Sem dependentes' },
        ],
      },
    ],
  },
]

const ADVANCED_FILTER_KEYS = ADVANCED_FILTER_SECTIONS.flatMap((section) =>
  section.fields.map((field) => field.key),
)

// Atalhos para os recortes que a escola pede toda semana — principalmente os que
// a migração criou. Cada um é só uma combinação dos filtros acima.
const QUICK_FILTERS: readonly QuickFilter[] = [
  { label: 'Sem CPF', icon: CreditCard, values: { has_document: 'false' } },
  {
    label: 'Não consegue logar',
    icon: KeyRound,
    values: { has_document: 'false', email_status: 'placeholder' },
  },
  {
    label: 'Aulas acabando',
    icon: TrendingDown,
    values: { lessons_left: 'ending' },
  },
  { label: 'Com dependentes', icon: Users, values: { has_dependents: 'true' } },
]

// Rótulo de cada valor, para o chip de filtro ativo dizer "CPF: Sem CPF" em vez de
// "has_document=false". Sai da própria configuração, então não há como um filtro novo
// aparecer sem legenda.
const FILTER_LABELS: Record<
  string,
  { field: string; options: Record<string, string> }
> = Object.fromEntries(
  ADVANCED_FILTER_SECTIONS.flatMap((section) =>
    section.fields.map((field) => [
      field.key,
      {
        field: field.label,
        options: Object.fromEntries(
          field.options.map((option) => [option.value, option.label]),
        ),
      },
    ]),
  ),
)

interface StudentsIndexProps {
  users: User[]
  school: School
  pagination: Pagination
  filters: Filters
  /** Total de alunos da escola sem nenhum filtro — para o cabeçalho dizer "37 de 1.240". */
  total_unfiltered: number
  wavecoin_enabled?: boolean
}

export default function StudentsIndex({
  users,
  school,
  pagination,
  filters,
  total_unfiltered,
  wavecoin_enabled,
}: StudentsIndexProps) {
  const [deleteUserId, setDeleteUserId] = useState<number | null>(null)
  const [deleteUserEmail, setDeleteUserEmail] = useState<string>('')

  const filterValues: FilterValues = Object.fromEntries(
    ADVANCED_FILTER_KEYS.map((key) => [
      key,
      filters[key as keyof Filters] || '',
    ]),
  )

  const activeChips: ActiveChip[] = ADVANCED_FILTER_KEYS.flatMap((key) => {
    const value = filterValues[key]
    if (!value) return []
    const labels = FILTER_LABELS[key]
    return [
      { key, label: `${labels.field}: ${labels.options[value] ?? value}` },
    ]
  })

  const hasAnyFilter = activeChips.length > 0 || Boolean(filters.search)

  const visit = (params: Record<string, unknown>) =>
    router.get(
      '/students',
      { ...filters, page: 1, ...params },
      {
        preserveState: true,
        preserveScroll: true,
      },
    )

  const applyAdvancedFilters = (values: FilterValues) => visit(values)

  const clearAllFilters = () =>
    visit({
      ...Object.fromEntries(ADVANCED_FILTER_KEYS.map((key) => [key, ''])),
      search: '',
    })

  const handleSort = (column: string) => {
    const newDirection =
      filters.sort_by === column && filters.sort_direction === 'asc'
        ? 'desc'
        : 'asc'
    visit({ sort_by: column, sort_direction: newDirection })
  }

  const handlePageChange = (page: number) => {
    router.get(
      '/students',
      {
        ...filters,
        page,
      },
      {
        preserveState: true,
        preserveScroll: true,
      },
    )
  }

  const handleDelete = (id: number) => {
    router.delete(`/users/${id}`, {
      onSuccess: () => {
        setDeleteUserId(null)
        setDeleteUserEmail('')
      },
    })
  }

  const getRoleBadge = (role: string) => {
    const variants = {
      owner: 'default',
      teacher: 'secondary',
      student: 'outline',
      manager: 'destructive',
    }
    const labels = {
      owner: 'Dono',
      teacher: 'Professor',
      student: 'Aluno',
      manager: 'Gerente',
    }
    return (
      <Badge variant={variants[role as keyof typeof variants] as any}>
        {labels[role as keyof typeof labels]}
      </Badge>
    )
  }

  // Contar quantos alunos têm dependentes
  const studentsWithDependents = users.filter(
    (u) => u.dependents_count && u.dependents_count > 0,
  ).length
  const totalDependents = users.reduce(
    (sum, u) => sum + (u.dependents_count || 0),
    0,
  )

  return (
    <DashboardLayout schoolName={school.name}>
      <div className="p-6 space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold">Alunos</h2>
            <p className="text-sm text-muted-foreground">
              Gerencie os alunos da escola
            </p>
          </div>
          <Button onClick={() => router.visit('/users/new?context=students')}>
            <UserPlus className="h-4 w-4 mr-2" />
            Novo Aluno
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Lista de Alunos</CardTitle>
            <CardDescription className="flex items-center gap-4 flex-wrap">
              {/* Com filtro aplicado, "1.240 alunos cadastrados" ao lado de uma lista de 37
                  linhas engana; o "de N" mantém o total da escola visível sem mentir sobre
                  o que está sendo mostrado. */}
              <span>
                {hasAnyFilter ? (
                  <>
                    <strong>{pagination.total_count}</strong> de{' '}
                    {total_unfiltered}{' '}
                    {total_unfiltered === 1 ? 'aluno' : 'alunos'}
                  </>
                ) : (
                  <>
                    {pagination.total_count}{' '}
                    {pagination.total_count === 1 ? 'aluno' : 'alunos'}{' '}
                    cadastrado{pagination.total_count === 1 ? '' : 's'}
                  </>
                )}
              </span>
              {studentsWithDependents > 0 && (
                <>
                  <span className="text-blue-600 dark:text-blue-400">•</span>
                  <span className="text-blue-600 dark:text-blue-400">
                    {studentsWithDependents} com dependentes ({totalDependents}{' '}
                    {totalDependents === 1 ? 'dependente' : 'dependentes'})
                  </span>
                </>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <FilterToolbar
              searchPlaceholder="Buscar por nome, e-mail, CPF ou telefone..."
              searchTerm={filters.search || ''}
              onSearch={(term) => visit({ search: term })}
              sections={ADVANCED_FILTER_SECTIONS}
              quickFilters={QUICK_FILTERS}
              values={filterValues}
              onApplyFilters={applyAdvancedFilters}
              dialogDescription="A busca por nome, e-mail, CPF ou telefone continua valendo junto com estes filtros."
              activeChips={activeChips}
              onRemoveFilter={(key) => visit({ [key]: '' })}
              onClearAll={clearAllFilters}
            />

            {/* Tabela */}
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      <Button
                        variant="ghost"
                        onClick={() => handleSort('name')}
                        className="flex items-center gap-1 p-0 h-auto font-semibold hover:bg-transparent"
                      >
                        Nome
                        <ArrowUpDown className="h-3 w-3" />
                      </Button>
                    </TableHead>
                    <TableHead>Email / CPF</TableHead>
                    <TableHead>Pacote Ativo</TableHead>
                    <TableHead>Progresso</TableHead>
                    {wavecoin_enabled && <TableHead>WaveCoins</TableHead>}
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={wavecoin_enabled ? 7 : 6}>
                        <EmptyResults
                          message="Nenhum aluno encontrado"
                          term={filters.search}
                          hasFilters={hasAnyFilter}
                          onClear={clearAllFilters}
                        />
                      </TableCell>
                    </TableRow>
                  ) : (
                    users.flatMap((user) => [
                      // Linha do titular
                      <TableRow
                        key={user.id}
                        className={
                          user.dependents_count && user.dependents_count > 0
                            ? 'bg-blue-500/5 dark:bg-blue-500/10 border-l-4 border-l-blue-500'
                            : ''
                        }
                      >
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <span>{user.name}</span>
                            {(user.dependents_count ?? 0) > 0 && (
                              <Badge
                                variant="secondary"
                                className="bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 text-xs"
                              >
                                {user.dependents_count}{' '}
                                {user.dependents_count === 1
                                  ? 'dependente'
                                  : 'dependentes'}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">
                          <div className="flex flex-col">
                            <span
                              className={
                                user.email.endsWith(IMPORT_EMAIL_DOMAIN)
                                  ? 'text-muted-foreground italic'
                                  : ''
                              }
                            >
                              {user.email}
                            </span>
                            {user.document && (
                              <span className="text-xs text-muted-foreground">
                                {applyCpfMask(user.document)}
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {user.has_active_package ? (
                            <div className="flex flex-col gap-1">
                              <Badge
                                variant="default"
                                className="bg-blue-500 w-fit"
                              >
                                {user.active_package_name}
                              </Badge>
                              <span className="text-xs text-muted-foreground">
                                {user.lessons_remaining} aulas restantes
                              </span>
                            </div>
                          ) : (
                            <Badge variant="outline">Sem pacote</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {(user.lessons_total ?? 0) > 0 ? (
                            <div className="flex flex-col gap-1">
                              <span className="text-sm font-medium">
                                {user.lessons_completed ?? 0}/
                                {user.lessons_total}
                              </span>
                              <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-primary transition-all"
                                  style={{
                                    width: `${((user.lessons_completed ?? 0) / (user.lessons_total ?? 1)) * 100}%`,
                                  }}
                                />
                              </div>
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground">
                              Nenhuma aula
                            </span>
                          )}
                        </TableCell>
                        {wavecoin_enabled && (
                          <TableCell>
                            {user.wavecoin_balance != null &&
                            user.wavecoin_balance > 0 ? (
                              <span className="text-sm font-medium text-primary">
                                {user.wavecoin_balance.toLocaleString()}
                              </span>
                            ) : (
                              <span className="text-xs text-muted-foreground">
                                0
                              </span>
                            )}
                          </TableCell>
                        )}
                        <TableCell>
                          {user.has_active_package ? (
                            <Badge variant="default" className="bg-green-500">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Ativo
                            </Badge>
                          ) : (
                            <Badge variant="secondary">Inativo</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              router.visit(`/users/${user.id}/edit`)
                            }
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              router.visit(`/users/${user.id}/dependents`)
                            }
                            title="Gerenciar dependentes"
                          >
                            <Users className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => router.visit(`/users/${user.id}`)}
                          >
                            Ver Detalhes
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => {
                                  setDeleteUserId(user.id)
                                  setDeleteUserEmail(user.email)
                                }}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>
                                  Confirmar Exclusão
                                </AlertDialogTitle>
                                <AlertDialogDescription>
                                  Tem certeza que deseja excluir o aluno{' '}
                                  <strong>{deleteUserEmail}</strong>? Esta ação
                                  não pode ser desfeita.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel
                                  onClick={() => {
                                    setDeleteUserId(null)
                                    setDeleteUserEmail('')
                                  }}
                                >
                                  Cancelar
                                </AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() =>
                                    deleteUserId && handleDelete(deleteUserId)
                                  }
                                >
                                  Excluir
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </TableCell>
                      </TableRow>,
                      // Linhas dos dependentes (se houver)
                      ...(user.dependents || []).map((dependent) => (
                        <TableRow
                          key={`dep-${dependent.id}`}
                          className="bg-blue-500/5 dark:bg-blue-500/10 border-l-4 border-l-blue-300 dark:border-l-blue-600"
                        >
                          <TableCell className="font-medium pl-8">
                            <div className="flex items-center gap-2">
                              <div className="flex items-center text-blue-600 dark:text-blue-400">
                                <div className="w-6 h-px bg-blue-300 dark:bg-blue-600 mr-2"></div>
                                <UserPlus className="h-4 w-4 mr-1" />
                              </div>
                              <span className="text-muted-foreground">
                                {dependent.name}
                              </span>
                              <Badge
                                variant="outline"
                                className="text-xs border-blue-300 dark:border-blue-600 text-blue-700 dark:text-blue-300"
                              >
                                Dependente
                              </Badge>
                            </div>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {dependent.email}
                          </TableCell>
                          <TableCell>
                            {dependent.has_active_package ? (
                              <div className="flex flex-col gap-1">
                                <Badge
                                  variant="outline"
                                  className="border-blue-300 dark:border-blue-600 text-blue-700 dark:text-blue-300 w-fit"
                                >
                                  Usa: {dependent.active_package_name}
                                </Badge>
                                <span className="text-xs text-muted-foreground">
                                  Pacote de {user.name}
                                </span>
                              </div>
                            ) : (
                              <Badge
                                variant="outline"
                                className="text-muted-foreground"
                              >
                                Sem pacote próprio
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <span className="text-xs text-muted-foreground">
                              -
                            </span>
                          </TableCell>
                          <TableCell>
                            {dependent.has_active_package ? (
                              <Badge variant="default" className="bg-green-500">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Ativo
                              </Badge>
                            ) : (
                              <Badge variant="secondary">Inativo</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right space-x-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                router.visit(`/users/${dependent.id}/edit`)
                              }
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                router.visit(`/users/${dependent.id}`)
                              }
                            >
                              Ver Detalhes
                            </Button>
                          </TableCell>
                        </TableRow>
                      )),
                    ])
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Paginação */}
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <PerPageSelect
                value={pagination.per_page}
                onChange={(perPage) => visit({ per_page: perPage })}
              />
              {pagination.total_pages > 1 && (
                <p className="text-sm text-muted-foreground">
                  Página {pagination.current_page} de {pagination.total_pages}
                </p>
              )}
              {pagination.total_pages > 1 && (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      handlePageChange(pagination.current_page - 1)
                    }
                    disabled={pagination.current_page === 1}
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Anterior
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      handlePageChange(pagination.current_page + 1)
                    }
                    disabled={
                      pagination.current_page === pagination.total_pages
                    }
                  >
                    Próxima
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  )
}
